import { apiConfig } from '@unreserved/configs';
import { API_ENDPOINTS, DEVICE_ID_KEY } from '@unreserved/constants';
import {
  AuthenticationApi,
  Configuration,
  type Middleware,
  HealthApi,
  ListingApi,
  NotificationsApi,
  OfferApi,
  PrometheusApi,
  UserApi,
  StaticApi,
  EnquiryApi,
  InspectionApi,
  type RefreshTokenResponseDto,
  KYCApi,
} from '@unreserved/sdk';
import { HttpStatus } from './http-status';

let actionOnMobileLogout = () => {};

export const setActionOnMobileLogout = (action: () => void) => {
  actionOnMobileLogout = action;
};

let actionOnTokenRefresh:
  | ((accessToken: string, refreshToken: string) => void | Promise<void>)
  | null = null;

export const setActionOnTokenRefresh = (
  action: (accessToken: string, refreshToken: string) => void | Promise<void>,
) => {
  actionOnTokenRefresh = action;
};

let sdkAccessToken: string | null = null;
let sdkRefreshToken: string | null = null;
let sdkDeviceId: string | null = null;

export const setSdkTokens = (
  accessToken: string | null,
  refreshToken: string | null,
) => {
  sdkAccessToken = accessToken;
  sdkRefreshToken = refreshToken;
};

export const setSdkDeviceId = (deviceId: string | null) => {
  sdkDeviceId = deviceId;
};

type FetchApi = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

const isReactNative =
  typeof navigator !== 'undefined' &&
  typeof (navigator as Navigator).product === 'string' &&
  (navigator as Navigator).product === 'ReactNative';

const middleware: Middleware = {
  async post({ fetch: wrappedFetch, url, init, response }) {
    if (response.status !== HttpStatus.UNAUTHORIZED) {
      return;
    }

    const headers = new Headers(init.headers as HeadersInit | undefined);

    if (headers.get('x-refresh-retry') === 'true') {
      return;
    }

    const header: Record<string, string> = { ...apiConfig.defaultHeaders };
    if (isReactNative) {
      header['x-device'] = 'mobile';
      header['x-refresh-token'] = sdkRefreshToken as string;
    }

    const refreshResponse = await fetch(
      `${apiConfig.baseUrl}${API_ENDPOINTS.AUTH_REFRESH}`,
      {
        method: 'POST',
        headers: header,
        credentials: isReactNative ? 'include' : 'omit',
      },
    );

    if (!refreshResponse.ok) {
      // Call logout API with deviceId before clearing tokens
      if (sdkDeviceId) {
        try {
          await fetch(`${apiConfig.baseUrl}${API_ENDPOINTS.AUTH_LOGOUT}`, {
            method: 'POST',
            headers: {
              ...apiConfig.defaultHeaders,
              'Content-Type': 'application/json',
              ...(isReactNative ? { 'x-device': 'mobile' } : {}),
            },
            credentials: isReactNative ? 'include' : 'omit',
            body: JSON.stringify({ deviceId: sdkDeviceId }),
          });
        } catch (error) {
          console.error('Logout failed:', error);
        }
      }

      // Clear deviceId from client
      setSdkDeviceId(null);
      if (
        typeof window !== 'undefined' &&
        typeof localStorage !== 'undefined'
      ) {
        localStorage.removeItem(DEVICE_ID_KEY);
      }

      isReactNative && actionOnMobileLogout();
      return;
    }

    try {
      const refreshData: RefreshTokenResponseDto = await refreshResponse.json();

      if (refreshData.data?.accessToken && refreshData.data?.refreshToken) {
        const newAccessToken = refreshData.data.accessToken;
        const newRefreshToken = refreshData.data.refreshToken;

        setSdkTokens(newAccessToken, newRefreshToken);

        // Update tokens in secure store (mobile)
        if (isReactNative && actionOnTokenRefresh) {
          await actionOnTokenRefresh(newAccessToken, newRefreshToken);
        }

        headers.set('Authorization', `Bearer ${newAccessToken}`);
      }
    } catch (error) {
      console.error('Error parsing refresh token response:', error);
    }

    headers.set('x-refresh-retry', 'true');

    return wrappedFetch(url, {
      ...init,
      headers,
    });
  },
};

const fetchApi: FetchApi = (input, init) => fetch(input, init);

const authMiddleware: Middleware = {
  async pre({ url, init }) {
    if (!isReactNative) {
      return;
    }

    const headers = new Headers(init.headers as HeadersInit | undefined);

    if (headers.get('Authorization')) {
      return;
    }

    let token: string | null = sdkAccessToken;

    if (token && token.trim() !== '') {
      headers.set('Authorization', `Bearer ${token}`);

      return {
        url,
        init: {
          ...init,
          headers,
        },
      };
    }
  },
};

const sdkConfiguration = new Configuration({
  basePath: apiConfig.baseUrl,
  fetchApi,
  credentials: isReactNative ? 'omit' : 'include',
  headers: {
    ...apiConfig.defaultHeaders,
    ...(isReactNative ? { 'x-device': 'mobile' } : {}),
  },
  accessToken: sdkAccessToken ?? '',
  middleware: isReactNative ? [authMiddleware, middleware] : [middleware],
});

export const authenticationApi = new AuthenticationApi(sdkConfiguration);
export const healthApi = new HealthApi(sdkConfiguration);
export const listingApi = new ListingApi(sdkConfiguration);
export const offerApi = new OfferApi(sdkConfiguration);
export const prometheusApi = new PrometheusApi(sdkConfiguration);
export const userApi = new UserApi(sdkConfiguration);
export const staticApi = new StaticApi(sdkConfiguration);
export const enquiryApi = new EnquiryApi(sdkConfiguration);
export const inspectionApi = new InspectionApi(sdkConfiguration);
export const kycApi = new KYCApi(sdkConfiguration);
export const notificationsApi = new NotificationsApi(sdkConfiguration);
